import { useEffect } from 'react';
import './HomeMainContent.css';
import frontPhoto from '../../../assets/home/home-page-edited.png';

export default function HomeMainContent() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.reveal'));

    const inViewport = (el) => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top <= vh * 0.9 && r.bottom >= vh * 0.1;
    };

    const revealNow = () => {
      for (const el of els) {
        if (!el.classList.contains('in') && inViewport(el)) {
          el.classList.add('in');
        }
      }
    };

    // Show anything already on screen
    revealNow();

    // IntersectionObserver (primary)
    let io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && entry.target instanceof HTMLElement) {
              entry.target.classList.add('in');
              io.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
      );
      els.forEach((el) => {
        if (!el.classList.contains('in')) io.observe(el);
      });
    }

    // Scroll/resize fallback + help on complex layouts
    let raf = null;
    const onScrollOrResize = () => {
      if (raf !== null) return;
      raf = window.requestAnimationFrame(() => {
        revealNow();
        raf = null;
      });
    };
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);

    // Safety pass after mount
    const timer = window.setTimeout(revealNow, 600);

    return () => {
      if (io) io.disconnect();
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (raf) window.cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <main className="content-component mtc">
      {/* HERO */}
      <section className="mtc-hero reveal" aria-labelledby="hero-title">
        <h1 id="hero-title" className="mtc-hero-title">Why We’re Here</h1>
        <p className="mtc-hero-sub">
          We build software that helps professionals make faster, better decisions—without replacing them.
        </p>
      </section>

      {/* HERO IMAGE */}
      <figure className="mtc-figure reveal">
        <img className="front-photo" src={frontPhoto} alt="Abstract network and sky" />
      </figure>

      {/* WHAT WE BUILD */}
      <hr className="mtc-hairline" />
      <section className="mtc-grid reveal" aria-labelledby="sect-what">
        <h2 id="sect-what" className="mtc-section-title">What we build</h2>
        <div className="mtc-copy">
          <h3 className="mtc-copy-head">Operational software for real-world missions</h3>
          <p>
            Myris Tech designs and ships production systems for teams who operate under pressure:
            secure web apps, data pipelines, and operator tooling that stand up quickly and hold up
            in austere or disconnected environments.
          </p>
          <p>
            Our approach is small, senior, and practical. Identity is first-class, telemetry is built-in,
            and deployment is repeatable—from a laptop at the edge to GovCloud. We favor clarity over
            complexity and ship features that move the mission forward.
          </p>
        </div>
      </section>

      {/* OUR CAPABILITIES */}
      <hr className="mtc-hairline" />
      <section className="mtc-grid reveal" aria-labelledby="sect-capabilities">
        <h2 id="sect-capabilities" className="mtc-section-title">Our capabilities</h2>
        <div className="mtc-copy">
          <h3 className="mtc-copy-head">Core expertise we bring to every engagement</h3>
          <ul className="naics-list">
            <li>
              <strong>Secure Full-Stack Development</strong> — Java/Spring, Node/React stacks with SSO/SAML,
              role-based access, and auditable actions.
            </li>
            <li>
              <strong>Cloud & DevSecOps Automation</strong> — Infrastructure-as-Code, hardened containers,
              CI/CD, SBOM, and supply-chain safeguards.
            </li>
            <li>
              <strong>AI-Enabled Decision Support</strong> — Retrieval pipelines, model hosting, and
              workflow integrations designed for constrained networks.
            </li>
            <li>
              <strong>Data Engineering & Integration</strong> — Streaming ingest, schema governance,
              and analytics with traceable provenance.
            </li>
            <li>
              <strong>Rapid Prototyping</strong> — Operator-in-the-loop iterations that converge on
              deployable tools, not demo-ware.
            </li>
          </ul>
        </div>
      </section>

      {/* OUR APPROACH */}
      <hr className="mtc-hairline" />
      <section className="mtc-grid reveal" aria-labelledby="sect-approach">
        <h2 id="sect-approach" className="mtc-section-title">Our approach</h2>
        <div className="mtc-copy">
          <h3 className="mtc-copy-head">Small senior teams. Production from day one.</h3>
          <p>
            We build with the people who will use the software. That means short feedback loops,
            clear acceptance criteria, and instrumentation that proves value in production.
          </p>
          <ul className="naics-list">
            <li><strong>Mission-aligned</strong> — outcomes over features; usefulness over novelty.</li>
            <li><strong>Security-first</strong> — identity, least privilege, and auditability by default.</li>
            <li><strong>Deployable anywhere</strong> — edge devices to GovCloud, connected or disconnected.</li>
            <li><strong>Observable</strong> — metrics, traces, and structured logs built-in.</li>
          </ul>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <hr className="mtc-hairline" />
      <section className="mtc-grid reveal" aria-labelledby="sect-customers">
        <h2 id="sect-customers" className="mtc-section-title">Who we serve</h2>
        <div className="mtc-copy">
          <h3 className="mtc-copy-head">Organizations with critical mandates</h3>
          <p>
            We partner with defense, intelligence, and public-sector teams operating in contested,
            high-consequence environments—where reliability and explainability matter.
          </p>
          <ul className="naics-list">
            <li>Defense & National Security</li>
            <li>Public Safety & Infrastructure</li>
            <li>Federal & State Agencies</li>
            <li>Select Commercial Operators</li>
          </ul>
        </div>
      </section>

      {/* HOW WE STARTED */}
      <hr className="mtc-hairline" />
      <section className="mtc-grid reveal" aria-labelledby="sect-history">
        <h2 id="sect-history" className="mtc-section-title">How we started</h2>
        <div className="mtc-copy">
          <h3 className="mtc-copy-head">Engineers from the field</h3>
          <p>
            Myris Tech began with engineers who cut their teeth building and operating systems for
            defense and public-sector partners. We saw the same pattern again and again: powerful
            technology that was hard to deploy, harder to maintain, and too slow to adapt.
          </p>
          <p>
            We founded Myris to close that gap—bringing product discipline to mission software and
            pairing it with DevSecOps fundamentals so updates are safe, auditable, and fast.
          </p>
        </div>
      </section>

      {/* IMPACT */}
      <hr className="mtc-hairline" />
      <section className="mtc-grid reveal" aria-labelledby="sect-impact">
        <h2 id="sect-impact" className="mtc-section-title">Impact we’re pursuing</h2>
        <div className="mtc-copy">
          <h3 className="mtc-copy-head">Software that earns trust in critical moments</h3>
          <p>
            Our goal is to give operators and analysts the tools to see what matters sooner and act
            with confidence. That means systems that are resilient, transparent, and accountable—
            and teams that stand behind what they ship.
          </p>
          <p>
            We measure our work by outcomes in the real world: faster decisions, safer communities,
            and missions completed with fewer surprises.
          </p>
        </div>
      </section>

      {/* NAICS CODES */}
      <hr className="mtc-hairline" />
      <section className="mtc-grid reveal" aria-labelledby="sect-naics">
        <h2 id="sect-naics" className="mtc-section-title">NAICS codes</h2>
        <div className="mtc-copy">
          <h3 className="mtc-copy-head">Primary & related classifications</h3>
          <ul className="naics-list">
            <li><strong>513210 — Software Publishers</strong></li>
            <li><strong>541511 — Custom Computer Programming Services</strong></li>
            <li><strong>541512 — Computer Systems Design Services</strong></li>
            <li><strong>541519 — Other Computer Related Services</strong></li>
          </ul>
        </div>
      </section>
    </main>
  );
}
